import { DraftSettings, Pokemon } from "../types/pokemon";
import { GENERATION_RANGES, LEGENDARY_IDS, MYTHICAL_IDS } from "../data/pokemonMeta";

const MIN_POOL_SIZE = 24;

// Note: typeFilter requires knowing each Pokemon's types. Since we only have IDs at this stage,
// type filtering is applied AFTER fetching in DraftScreen. Pool validation estimates conservatively.
export function generateDraftPool(settings: DraftSettings, excludeIds: number[] = []): number[] {
  const excluded = new Set(excludeIds);
  const pool: number[] = [];

  for (const gen of settings.enabledGenerations) {
    const range = GENERATION_RANGES[gen];
    if (!range) continue;
    for (let id = range[0]; id <= range[1]; id++) {
      if (excluded.has(id)) continue;
      if (!settings.allowLegendaries && LEGENDARY_IDS.has(id)) continue;
      if (!settings.allowMythicals && MYTHICAL_IDS.has(id)) continue;
      pool.push(id);
    }
  }

  return pool;
}

// Called in DraftScreen after fetching Pokemon data to apply type filter
export function filterByType(pokemon: Pokemon[], typeFilter: string | null): Pokemon[] {
  if (!typeFilter) return pokemon;
  return pokemon.filter(p => p.types.includes(typeFilter));
}

export function validatePool(settings: DraftSettings): { valid: boolean; count: number; message?: string } {
  const pool = generateDraftPool(settings);
  if (pool.length >= MIN_POOL_SIZE) {
    return { valid: true, count: pool.length };
  }
  return {
    valid: false,
    count: pool.length,
    message: `Pas assez de Pokemon disponibles (${pool.length}/${MIN_POOL_SIZE}). Elargissez vos filtres.`,
  };
}

export function expandPoolIfNeeded(
  currentPool: number[],
  settings: DraftSettings,
  excludeIds: number[],
  needed: number
): { pool: number[]; message?: string } {
  if (currentPool.length >= needed) return { pool: currentPool };

  const excluded = new Set(excludeIds);
  const allGens = [1, 2, 3, 4, 5, 6, 7, 8, 9];
  const missingGens = allGens.filter(g => !settings.enabledGenerations.includes(g));

  let expanded = [...currentPool];
  for (const gen of missingGens) {
    const range = GENERATION_RANGES[gen];
    if (!range) continue;
    for (let id = range[0]; id <= range[1]; id++) {
      if (excluded.has(id)) continue;
      if (!settings.allowLegendaries && LEGENDARY_IDS.has(id)) continue;
      if (!settings.allowMythicals && MYTHICAL_IDS.has(id)) continue;
      expanded.push(id);
    }
    if (expanded.length >= needed) {
      return { pool: expanded, message: "Pool elargi automatiquement (generations ajoutees)" };
    }
  }

  return { pool: expanded, message: "Pool elargi automatiquement" };
}
